import { useState, useEffect } from 'react';
import { Link, useLocation, Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import useIdleLogout from '../../hooks/useIdleLogout';
import DashboardTour from './DashboardTour';
import TwoFactorReminder from './TwoFactorReminder';
import {
  LayoutDashboard, Users, Calendar, Phone, Settings, ChevronLeft,
  LogOut, Menu, Bell, BarChart3, Zap, AlertTriangle, X as XIcon, Plus, Minus, Sun, Moon, MessageSquare,
  Sparkles, BookOpen, Bot, Wallet,
  // Industry-specific icons referenced by INDUSTRY_CONFIG
  UtensilsCrossed, Clock, Stethoscope, HeartPulse, HardHat, FileText, Scissors,
  Scale, FolderOpen, Home, Building, TrendingUp, Package, ShoppingCart, BarChart2,
} from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import { LocationProvider } from '../../context/LocationContext';
import LocationSwitcher from './LocationSwitcher';
import { LiveCallBanner } from './LiveCallCard';

// Map of icon name (string) → lucide-react component, used to resolve icons referenced as strings in INDUSTRY_CONFIG.
const ICONS = {
  Calendar, UtensilsCrossed, Clock, Stethoscope, HeartPulse, HardHat, FileText,
  Scissors, Scale, FolderOpen, Home, Building, TrendingUp, Package, ShoppingCart, BarChart2,
};

// Per-industry overrides for the sidebar booking item + extra nav links.
// The "bookingLabel" replaces the default "Appointments" label/icon. Each entry in
// "extraNav" is rendered right after the booking item.
const INDUSTRY_CONFIG = {
  restaurant: {
    bookingLabel: "Reservations",
    bookingIcon: "UtensilsCrossed",
    extraNav: [{ label: "Waitlist", path: "waitlist", icon: "Clock" }],
  },
  clinic: {
    bookingLabel: "Appointments",
    bookingIcon: "Stethoscope",
    extraNav: [{ label: "Patients", path: "patients", icon: "HeartPulse" }],
  },
  medical: {
    bookingLabel: "Appointments",
    bookingIcon: "Stethoscope",
    extraNav: [{ label: "Patients", path: "patients", icon: "HeartPulse" }],
  },
  contractor: {
    bookingLabel: "Jobs",
    bookingIcon: "HardHat",
    extraNav: [{ label: "Estimates", path: "estimates", icon: "FileText" }],
  },
  general_contractor: {
    bookingLabel: "Jobs",
    bookingIcon: "HardHat",
    extraNav: [{ label: "Estimates", path: "estimates", icon: "FileText" }],
  },
  salon: {
    bookingLabel: "Bookings",
    bookingIcon: "Scissors",
    extraNav: [],
  },
  hair_salon: {
    bookingLabel: "Bookings",
    bookingIcon: "Scissors",
    extraNav: [],
  },
  legal: {
    bookingLabel: "Consultations",
    bookingIcon: "Scale",
    extraNav: [{ label: "Matters", path: "matters", icon: "FolderOpen" }],
  },
  law_firm: {
    bookingLabel: "Consultations",
    bookingIcon: "Scale",
    extraNav: [{ label: "Matters", path: "matters", icon: "FolderOpen" }],
  },
  real_estate: {
    bookingLabel: "Showings",
    bookingIcon: "Home",
    extraNav: [{ label: "Listings", path: "listings", icon: "Building" }],
  },
  dental: {
    bookingLabel: "Appointments",
    bookingIcon: "Stethoscope",
    extraNav: [{ label: "Patients", path: "patients", icon: "HeartPulse" }],
  },
  other: {
    bookingLabel: "Appointments",
    bookingIcon: "Calendar",
    extraNav: [],
  },
};

export default function DashboardLayout() {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const handleStartTour = () => {
    setMobileOpen(false);
    // Reset the "tour seen" flag so the welcome modal definitely opens, then
    // dispatch the shared event the tour component listens for.
    if (user?.business?.id) {
      localStorage.removeItem(`nextbell-dashboard-tour-v1-${user.business.id}`);
    }
    window.dispatchEvent(new Event('nextbell:start-tour'));
  };

  // Auto-logout after 15 minutes of inactivity (mouse, keyboard, scroll, touch).
  useIdleLogout({
    timeoutMs: 15 * 60 * 1000,
    onTimeout: async () => {
      await logout();
      navigate('/login?reason=idle');
    },
  });

  const business = user?.business;

  // Resolve industry config (fallback to "other" for any unknown niche).
  // The fallback's extraNav is [] so unknown niches NEVER inherit extra items.
  const niche = (business?.niche || 'other').toString().toLowerCase().trim();
  const industryConfig = INDUSTRY_CONFIG[niche] ?? INDUSTRY_CONFIG['other'];

  // Build the sidebar item list. The booking item's label and icon come from
  // industryConfig.bookingLabel / industryConfig.bookingIcon. Industry extras
  // (e.g. Waitlist, Patients) sit right after it.
  const safeExtraNav = (industryConfig.extraNav || [])
    .map(item => ({
      label: item.label,
      path: `/dashboard/${item.path}`,
      icon: ICONS[item.icon] || Calendar,
    }));

  const bookingItem = {
    label: industryConfig.bookingLabel,
    path: '/dashboard/appointments',
    icon: ICONS[industryConfig.bookingIcon] || Calendar,
    tour: 'appointments',
  };

  // Sidebar nav grouped into labelled sections (Vapi-style). All existing items,
  // paths, icons, tours and testids are preserved — they are just organised into
  // sections. Industry extras (Waitlist, Patients, …) sit in COMMUNICATION next
  // to the booking item. Settings lives on its own at the bottom of the nav.
  const navSections = [
    {
      label: 'Main',
      items: [
        { label: 'Dashboard', path: '/dashboard', icon: LayoutDashboard, tour: 'home' },
      ],
    },
    {
      label: 'Communication',
      items: [
        { label: 'Calls', path: '/dashboard/calls', icon: Phone, tour: 'calls' },
        { label: 'Messaging', path: '/dashboard/messaging', icon: MessageSquare, tour: 'messaging' },
        bookingItem,
        ...safeExtraNav,
      ],
    },
    {
      label: 'Customers',
      items: [
        { label: 'Leads', path: '/dashboard/leads', icon: Users, tour: 'leads' },
      ],
    },
    {
      label: 'AI',
      items: [
        { label: 'Ava', path: '/dashboard/ava', icon: Bot, tour: 'ava' },
      ],
    },
    {
      label: 'Insights',
      items: [
        { label: 'Analytics', path: '/dashboard/analytics', icon: BarChart3, tour: 'analytics' },
      ],
    },
    {
      label: 'Manage',
      items: [
        { label: 'Credits', path: '/dashboard/credits', icon: Wallet, tour: 'credits' },
        { label: 'Settings', path: '/dashboard/settings', icon: Settings, tour: 'settings' },
      ],
    },
  ];

  // Flat list kept for the mobile drawer (single scrollable column).
  const navItems = navSections.flatMap(s => s.items);

  // Shared className builder for a nav link in the desktop sidebar. Active item
  // gets a subtle filled pill with the mint/teal accent (#34d399 / emerald-400).
  const navLinkClass = (active, isCollapsed) =>
    `flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-all ${
      active
        ? 'bg-blue-50 text-blue-700 font-medium dark:bg-blue-500/15 dark:text-blue-300'
        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50 dark:text-slate-400 dark:hover:text-white dark:hover:bg-white/[0.05]'
    } ${isCollapsed ? 'justify-center' : ''}`;

  return (
    <LocationProvider>
    <div className="min-h-screen flex bg-[#f9f8f4] dark:bg-[#0a0a0a] transition-colors max-w-[100vw] overflow-x-hidden">
      {/* Sidebar */}
      <aside data-testid="dashboard-sidebar"
        className={`hidden md:flex flex-col ${collapsed ? 'w-16' : 'w-64'} bg-[#f9f8f4] dark:bg-[#0a0a0a] border-r border-gray-200/50 dark:border-white/5 transition-all duration-300 fixed inset-y-0 left-0 z-40`}>
        <div className="p-4 flex items-center justify-between border-b border-gray-100 dark:border-white/5">
          {!collapsed && <Link to="/" className="font-heading font-semibold text-gray-900 dark:text-white">NextBell</Link>}
          <button onClick={() => setCollapsed(!collapsed)} className="text-gray-400 hover:text-gray-600 dark:hover:text-white p-1">
            <ChevronLeft className={`w-4 h-4 transition-transform ${collapsed ? 'rotate-180' : ''}`} />
          </button>
        </div>
        <nav className="flex-1 overflow-y-auto py-4 px-2 space-y-5">
          {navSections.map(section => (
            <div key={section.label} className="space-y-1">
              {!collapsed && (
                <p className="px-3 pb-1 text-[11px] font-medium uppercase tracking-wider text-gray-400 dark:text-gray-500">
                  {section.label}
                </p>
              )}
              {section.items.map(item => {
                const active = location.pathname === item.path || (item.path !== '/dashboard' && location.pathname.startsWith(item.path));
                return (
                  <Link key={item.path} to={item.path} data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                    data-tour={item.tour}
                    className={navLinkClass(active, collapsed)}>
                    <item.icon className="w-5 h-5 flex-shrink-0" />
                    {!collapsed && <span>{item.label}</span>}
                  </Link>
                );
              })}
            </div>
          ))}
        </nav>
        <div className="px-3 pb-2 border-t border-gray-100 dark:border-white/5 pt-3">
          <button data-testid="sidebar-onboarding-btn" onClick={handleStartTour}
            title="Replay the welcome tour"
            className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-gray-600 dark:text-slate-400 hover:text-blue-700 dark:hover:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-500/15 w-full transition-colors ${collapsed ? 'justify-center' : ''}`}>
            <Sparkles className="w-5 h-5 flex-shrink-0" />
            {!collapsed && <span>Onboarding</span>}
          </button>
        </div>
        <div className="p-3 border-t border-gray-100 dark:border-white/5">
          <button data-testid="sidebar-logout-btn" onClick={handleLogout}
            className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-gray-500 dark:text-slate-400 hover:text-red-600 hover:bg-red-50 dark:hover:text-red-300 dark:hover:bg-red-500/10 w-full transition-colors ${collapsed ? 'justify-center' : ''}`}>
            <LogOut className="w-5 h-5" />
            {!collapsed && <span>Log out</span>}
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className={`flex-1 min-w-0 flex flex-col ${collapsed ? 'md:ml-16' : 'md:ml-64'} transition-all duration-300`}>
        <header data-testid="dashboard-topbar" className="sticky top-0 z-30 bg-[#f9f8f4]/90 dark:bg-[#0a0a0a]/85 backdrop-blur-xl border-b border-gray-200/50 dark:border-white/5 px-4 md:px-8 h-14 flex items-center justify-between">
          <div className="flex items-center gap-2 md:gap-2.5 min-w-0 flex-1 md:flex-initial">
            <button onClick={() => setMobileOpen(!mobileOpen)} data-testid="mobile-menu-btn" aria-label="Toggle menu" className="md:hidden text-gray-900 dark:text-white flex-shrink-0"><Menu className="w-5 h-5" /></button>
            <h2 className="text-gray-900 dark:text-white font-heading font-medium text-sm truncate">{business?.name || 'My Business'}</h2>
            <span className={`hidden sm:inline text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full font-medium flex-shrink-0 ${planBadgeStyles(business?.plan)}`}>
              {business?.plan || 'starter'}
            </span>
            <LocationSwitcher />
          </div>
          <div className="flex items-center gap-2 md:gap-4">
            <div className="hidden lg:flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-gray-200 dark:border-white/10 bg-white/60 dark:bg-white/[0.04]">
              <span className="relative flex h-1.5 w-1.5">
                <span className="absolute inline-flex h-full w-full rounded-full bg-blue-500 opacity-60 animate-ping" />
                <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-blue-500" />
              </span>
              <span className="text-[11px] font-medium text-gray-700 dark:text-slate-300">AI Active</span>
            </div>
            <div data-testid="usage-min-pill" className="flex items-center gap-1 text-[10px] md:text-[11px] font-medium px-2 md:px-2.5 py-1 rounded-full border border-gray-200 dark:border-white/10 bg-white/60 dark:bg-white/[0.04]">
              <span className="text-gray-900 dark:text-white tabular-nums">{business?.minutes_used || 0}</span>
              <span className="text-gray-400 dark:text-slate-500">/{business?.minutes_cap || 600}<span className="hidden sm:inline"> min</span><span className="sm:hidden">m</span></span>
            </div>
            <div data-testid="usage-sms-pill" className="flex items-center gap-1 text-[10px] md:text-[11px] font-medium px-2 md:px-2.5 py-1 rounded-full border border-gray-200 dark:border-white/10 bg-white/60 dark:bg-white/[0.04]">
              <span className="text-gray-900 dark:text-white tabular-nums">{business?.sms_used || 0}</span>
              <span className="text-gray-400 dark:text-slate-500">/{business?.sms_cap || 100}<span className="hidden sm:inline"> SMS</span><span className="sm:hidden">s</span></span>
            </div>
            <ThemeToggleButton />
            <button data-testid="notifications-btn" aria-label="Notifications" className="hidden sm:flex w-8 h-8 rounded-full hover:bg-gray-100 dark:hover:bg-white/[0.07] items-center justify-center text-gray-500 hover:text-gray-900 dark:text-slate-400 dark:hover:text-white transition-colors">
              <Bell className="w-4 h-4" />
            </button>
            <div data-testid="user-avatar" className="hidden sm:flex w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-blue-700 dark:from-blue-500 dark:to-blue-700 items-center justify-center text-white text-xs font-semibold shadow-sm flex-shrink-0">
              {user?.name?.charAt(0)?.toUpperCase() || 'U'}
            </div>
          </div>
        </header>
        <main className="flex-1 p-4 md:p-8 min-w-0 max-w-full overflow-x-hidden">
          <div className="max-w-6xl mx-auto space-y-4">
            <LiveCallBanner />
          </div>
          <Outlet />
        </main>
      </div>

      {/* Mobile sidebar drawer */}
      {mobileOpen && (
        <>
          <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-40 md:hidden" onClick={() => setMobileOpen(false)} />
          <div className="fixed inset-y-0 left-0 w-[78%] max-w-[280px] bg-white dark:bg-[#0a0a0a] border-r border-gray-200 dark:border-white/10 z-50 md:hidden flex flex-col">
            <div className="p-4 flex items-center justify-between border-b border-gray-100 dark:border-white/5">
              <Link to="/" className="font-heading font-semibold text-gray-900 dark:text-white">NextBell</Link>
              <button onClick={() => setMobileOpen(false)} aria-label="Close menu" className="text-gray-400 hover:text-gray-700 dark:hover:text-white p-1">
                <XIcon className="w-5 h-5" />
              </button>
            </div>
            <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-1">
              {navItems.map(item => {
                const active = location.pathname === item.path || (item.path !== '/dashboard' && location.pathname.startsWith(item.path));
                return (
                  <Link key={item.path} to={item.path} onClick={() => setMobileOpen(false)}
                    className={`flex items-center gap-3 px-3 py-3 rounded-xl text-sm transition-colors ${
                      active
                        ? 'bg-blue-50 text-blue-700 font-medium dark:bg-blue-500/15 dark:text-blue-300'
                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50 dark:text-slate-300 dark:hover:text-white dark:hover:bg-white/[0.05]'
                    }`}>
                    <item.icon className="w-5 h-5 flex-shrink-0" /><span>{item.label}</span>
                  </Link>
                );
              })}
              <button data-testid="mobile-onboarding-btn" onClick={handleStartTour}
                className="flex items-center gap-3 px-3 py-3 rounded-xl text-sm w-full text-left transition-colors text-gray-600 dark:text-slate-300 hover:text-blue-700 dark:hover:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-500/15">
                <Sparkles className="w-5 h-5 flex-shrink-0" /><span>Onboarding</span>
              </button>
            </nav>
            <div className="p-3 border-t border-gray-100 dark:border-white/5">
              <div className="flex items-center gap-3 px-2 py-2 mb-2">
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center text-white text-xs font-semibold flex-shrink-0">
                  {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{user?.name || 'User'}</p>
                  <p className="text-[11px] text-gray-500 dark:text-slate-400 truncate">{user?.email}</p>
                </div>
              </div>
              <button onClick={handleLogout} data-testid="mobile-logout-btn"
                className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm text-gray-500 dark:text-slate-400 hover:text-red-600 hover:bg-red-50 dark:hover:text-red-300 dark:hover:bg-red-500/10 w-full transition-colors">
                <LogOut className="w-5 h-5" /><span>Log out</span>
              </button>
            </div>
          </div>
        </>
      )}

      {/* Low / out-of-minutes alert banner */}
      <LowMinutesBanner business={business} />
      {/* First-time guided tour (welcome modal + spotlight) */}
      <DashboardTour />
      <TwoFactorReminder />
    </div>
    </LocationProvider>
  );
}

function LowMinutesBanner({ business }) {
  const navigate = useNavigate();
  const [dismissed, setDismissed] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);

  const minUsed = Number(business?.minutes_used || 0);
  const minCap = Number(business?.minutes_cap || 0);
  const plan = (business?.plan || '').toString().toLowerCase();
  const usagePercent = minCap > 0 ? Math.round((minUsed / minCap) * 100) : 0;
  const isHardCap = usagePercent >= 100;
  const showBanner = (
    plan !== 'enterprise' &&
    minCap > 0 &&
    usagePercent >= 90 &&
    (isHardCap || (!dismissed && sessionStorage.getItem('minutes_popup_dismissed') !== '1'))
  );

  if (!showBanner) return null;

  const handleDismiss = () => {
    sessionStorage.setItem('minutes_popup_dismissed', '1');
    setDismissed(true);
  };

  return (
    <>
      <div data-testid="low-minutes-banner" className={`fixed bottom-4 left-1/2 -translate-x-1/2 z-50 w-[calc(100%-2rem)] max-w-2xl rounded-2xl shadow-2xl border ${isHardCap ? 'bg-red-600 border-red-700 text-white dark:bg-red-500 dark:border-red-600' : 'bg-white border-amber-200 dark:bg-[#1a1a1c] dark:border-amber-500/40'}`}>
        <div className="flex items-start gap-3 p-4">
          <div className={`flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center ${isHardCap ? 'bg-white/20' : 'bg-amber-50 dark:bg-amber-500/15'}`}>
            {isHardCap ? <AlertTriangle className="w-5 h-5 text-white" /> : <Zap className="w-5 h-5 text-amber-600 dark:text-amber-400" />}
          </div>
          <div className="flex-1 min-w-0">
            <p className={`text-sm font-medium ${isHardCap ? 'text-white' : 'text-gray-900 dark:text-white'}`}>
              {isHardCap
                ? <>You've hit your minute limit. Ava can't take calls until you add more minutes.</>
                : <>You've used {usagePercent}% of your monthly minutes. Add 100 more for $25 to keep Ava running.</>}
            </p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              onClick={() => setModalOpen(true)}
              data-testid="low-minutes-add-btn"
              className={`text-xs font-medium rounded-full px-4 py-2 transition-colors ${isHardCap ? 'bg-white text-red-700 hover:bg-red-50' : 'bg-blue-600 hover:bg-blue-500 text-white'}`}>
              Add Minutes
            </button>
            {!isHardCap && (
              <button
                onClick={handleDismiss}
                data-testid="low-minutes-dismiss-btn"
                className="text-xs font-medium text-gray-400 hover:text-gray-700 dark:text-slate-500 dark:hover:text-slate-200 px-2">
                Dismiss
              </button>
            )}
          </div>
        </div>
      </div>

      {modalOpen && (
        <BuyMinutesModal
          onClose={() => setModalOpen(false)}
          onGoToSettings={() => { setModalOpen(false); navigate('/dashboard/settings?tab=billing'); }}
        />
      )}
    </>
  );
}

function BuyMinutesModal({ onClose, onGoToSettings }) {
  const [blocks, setBlocks] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { api } = useAuth();
  const total = blocks * 25;
  const minutesAdded = blocks * 100;

  const buy = async () => {
    setError(''); setLoading(true);
    try {
      const { data } = await api.post('/billing/buy-extra-minutes', { blocks });
      window.location.href = data.url;
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not start checkout.');
      setLoading(false);
    }
  };

  return (
    <div data-testid="buy-minutes-modal" className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-white dark:bg-[#1a1a1c] dark:border dark:border-white/10 rounded-2xl max-w-sm w-full shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between p-5 pb-3">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-blue-50 dark:bg-blue-500/15 flex items-center justify-center">
              <Zap className="w-4 h-4 text-blue-600 dark:text-blue-400" />
            </div>
            <h3 className="font-heading text-lg font-semibold text-gray-900 dark:text-white">Add minutes</h3>
          </div>
          <button onClick={onClose} aria-label="Close" className="text-gray-400 hover:text-gray-700 dark:text-slate-500 dark:hover:text-slate-200">
            <XIcon className="w-5 h-5" />
          </button>
        </div>
        <div className="px-5 pb-5">
          <p className="text-sm text-gray-500 dark:text-slate-400 mb-4">100 minutes for $25 each. Credited instantly.</p>
          <div className="flex items-center gap-3 mb-4">
            <div className="flex items-center bg-gray-100 dark:bg-white/[0.05] rounded-full">
              <button onClick={() => setBlocks(b => Math.max(1, b - 1))} disabled={blocks <= 1} className="w-9 h-9 flex items-center justify-center text-gray-600 dark:text-slate-300 disabled:opacity-30">
                <Minus className="w-4 h-4" />
              </button>
              <span className="w-10 text-center text-sm font-medium text-gray-900 dark:text-white">{blocks}</span>
              <button onClick={() => setBlocks(b => Math.min(20, b + 1))} disabled={blocks >= 20} className="w-9 h-9 flex items-center justify-center text-gray-600 dark:text-slate-300 disabled:opacity-30">
                <Plus className="w-4 h-4" />
              </button>
            </div>
            <span className="text-sm text-gray-500 dark:text-slate-400">× 100 min</span>
            <span className="text-sm font-medium text-gray-900 dark:text-white ml-auto">${total}</span>
          </div>
          <button onClick={buy} disabled={loading} data-testid="buy-minutes-confirm"
            className="w-full bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white text-sm font-medium rounded-full py-2.5">
            {loading ? 'Redirecting...' : `Buy ${minutesAdded} min for $${total}`}
          </button>
          <button onClick={onGoToSettings} className="w-full text-xs text-gray-500 hover:text-gray-700 dark:text-slate-500 dark:hover:text-slate-300 mt-2">
            Or open Settings, then Billing
          </button>
          {error && <div className="mt-3 bg-red-50 dark:bg-red-500/15 border border-red-200 dark:border-red-500/30 rounded-xl px-3 py-2 text-sm text-red-700 dark:text-red-300">{error}</div>}
        </div>
      </div>
    </div>
  );
}

function ThemeToggleButton() {
  const { theme, toggle } = useTheme();
  return (
    <button
      type="button"
      onClick={toggle}
      data-testid="theme-toggle-btn"
      aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
      title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
      className="w-8 h-8 rounded-full hover:bg-gray-100 dark:hover:bg-white/[0.07] flex items-center justify-center text-gray-500 hover:text-gray-900 dark:text-slate-400 dark:hover:text-yellow-300 transition-colors">
      {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
    </button>
  );
}

// Subtle pill-style plan badge that reads cleanly in both themes (no more solid block of color)
function planBadgeStyles(plan) {
  switch ((plan || 'starter').toLowerCase()) {
    case 'growth': return 'bg-blue-50 text-blue-700 border border-blue-200 dark:bg-blue-500/15 dark:text-blue-300 dark:border-blue-500/30';
    case 'agency': return 'bg-purple-50 text-purple-700 border border-purple-200 dark:bg-purple-500/15 dark:text-purple-300 dark:border-purple-500/30';
    case 'custom':
    case 'enterprise': return 'bg-amber-50 text-amber-700 border border-amber-200 dark:bg-amber-500/15 dark:text-amber-300 dark:border-amber-500/30';
    default: return 'bg-gray-100 text-gray-600 border border-gray-200 dark:bg-white/[0.06] dark:text-slate-300 dark:border-white/10';
  }
}

